import type {
  OfficialPaymentAttemptStatus,
  OfficialPaymentDirectiveV1,
  OfficialPaymentExecutionResultV1,
} from "@billiard/contracts";

interface OfficialPaymentStatusCopy {
  title: string;
  description: string;
  nextStep: string;
}

const statusCopy: Record<OfficialPaymentAttemptStatus, OfficialPaymentStatusCopy> = {
  PENDING: {
    title: "正在等待渠道确认",
    description:
      "付款已经发起，系统正在等待渠道返回结果。现在不能重复创建付款，也不能把这笔钱当作已到账。",
    nextStep: "请保留当前订单；需要核对时，只查询这一笔付款。",
  },
  RESULT_UNKNOWN: {
    title: "付款结果暂时无法确认",
    description:
      "这次付款可能已经被渠道接收，也可能还没有结果。系统不会把它当作到账，也不会新建第二笔付款。",
    nextStep: "请使用同一笔付款继续查询，不要让顾客再次付款。",
  },
  SUCCESS: {
    title: "渠道已确认到账",
    description: "渠道已返回这笔付款成功的确认结果。",
    nextStep: "请按系统给出的后续步骤完成本单，不要再创建付款。",
  },
  NOTPAY: {
    title: "顾客尚未付款",
    description: "渠道已确认这笔付款没有完成，系统没有把它当作到账。",
    nextStep: "可以按系统允许的方式选择其他付款方式。",
  },
  CLOSED: {
    title: "付款已关闭",
    description: "这笔付款已经结束，系统没有把它当作到账。",
    nextStep: "可以按系统允许的方式选择其他付款方式。",
  },
  FAILED_TERMINAL: {
    title: "付款处理失败，需要人工核对",
    description:
      "渠道或系统已给出不能继续自动处理的结果。系统不会把这笔钱当作到账，也不会自动换一种付款方式。",
    nextStep: "请保留订单和付款记录，交由负责人按工作中心提示核对。",
  },
  MANUAL_REVIEW: {
    title: "付款需要人工核对",
    description: "系统发现这笔付款需要负责人确认。核对完成前，不能把它当作到账，也不能新建付款。",
    nextStep: "请保留订单和付款记录，交由负责人按工作中心提示核对。",
  },
};

export interface OfficialPaymentPresentation extends OfficialPaymentStatusCopy {
  status: OfficialPaymentAttemptStatus;
  providerEvidence: OfficialPaymentExecutionResultV1["providerEvidence"];
  directive: OfficialPaymentDirectiveV1;
  canCreateNewPayment: boolean;
  canRecognizeAsPaid: boolean;
}

/**
 * Pure UI adapter for the frozen official-payment contract.
 *
 * The UI never derives whether an attempt may be replaced or recognized as paid:
 * both values are copied directly from the domain directive.
 */
export function officialPaymentPresentation(
  execution: Pick<OfficialPaymentExecutionResultV1, "attempt" | "directive" | "providerEvidence">,
): OfficialPaymentPresentation {
  const { attempt, directive, providerEvidence } = execution;
  const copy =
    attempt.status === "SUCCESS" && providerEvidence === "SANDBOX"
      ? {
          title: "测试渠道显示成功，不能确认到账",
          description: "这只是测试渠道结果，不是已验收商户渠道的真实到账证明。",
          nextStep: "请保留订单并交由负责人核对，不能据此结账或让顾客重复付款。",
        }
      : statusCopy[attempt.status];
  return {
    status: attempt.status,
    providerEvidence,
    ...copy,
    directive,
    canCreateNewPayment: directive.canCreateNewAttempt,
    canRecognizeAsPaid: directive.canRecognizeRevenue,
  };
}
